/**
 * Rotas folha na mesma ordem do menu lateral (sidebar).
 * Usado quando o usuário não tem abas favoritas para definir a página inicial.
 */
export type AccessibleRoute = {
  path: string;
  requiredPermission?: string;
  superAdminOnly?: boolean;
};

export const ACCESSIBLE_ROUTES_ORDER: AccessibleRoute[] = [
  { path: '/sensoriamento/sensores', requiredPermission: 'sidebar_sensoriamento_sensores' },
  { path: '/sensoriamento/relatorios', requiredPermission: 'sidebar_sensoriamento_relatorios' },
  { path: '/product-engineering/eng-produto', requiredPermission: 'sidebar_product_engineering_product' },
  { path: '/product-engineering/componentes', requiredPermission: 'sidebar_product_engineering_componentes' },
  { path: '/product-engineering/processos', requiredPermission: 'sidebar_product_engineering_processo' },
  { path: '/production/planning/ordens', requiredPermission: 'sidebar_production_planning_panel' },
  { path: '/production/planning/processos', requiredPermission: 'sidebar_production_processos' },
  { path: '/production/schedule', requiredPermission: 'sidebar_production_schedule' },
  { path: '/production/resources', requiredPermission: 'sidebar_production_resources' },
  { path: '/production/apontamento', requiredPermission: 'sidebar_production_apontamento' },
  { path: '/production/control/auditoria', requiredPermission: 'sidebar_production_control_audit' },
  { path: '/production/control/nao-conformidades', requiredPermission: 'sidebar_production_control_nc' },
  { path: '/production/control/lotes', requiredPermission: 'sidebar_production_control_lotes' },
  { path: '/production/monitoring', requiredPermission: 'sidebar_production_monitoring' },
  { path: '/production/factory-view', requiredPermission: 'sidebar_production_factory_view' },
  { path: '/production/dashboards', requiredPermission: 'sidebar_production_dashboards' },
  { path: '/production/melhoria', requiredPermission: 'sidebar_production_melhoria' },
  { path: '/production/control/relatorios', requiredPermission: 'sidebar_production_reports' },
  { path: '/maintenance/control', requiredPermission: 'sidebar_maintenance_control' },
  { path: '/maintenance/planning', requiredPermission: 'sidebar_maintenance_planning' },
  { path: '/maintenance/dashboards', requiredPermission: 'sidebar_maintenance_dashboards' },
  { path: '/maintenance/parametrizacao/sensores', requiredPermission: 'sidebar_sensores' },
  { path: '/quality/parametros', requiredPermission: 'sidebar_quality_parametros' },
  { path: '/quality/checklist', requiredPermission: 'sidebar_quality_checklist' },
  { path: '/quality/regras', requiredPermission: 'sidebar_quality_regras' },
  { path: '/quality/auditoria', requiredPermission: 'sidebar_quality_auditoria' },
  { path: '/quality/dashboards', requiredPermission: 'sidebar_quality_dashboards' },
  { path: '/quality/planejamento', requiredPermission: 'sidebar_quality_planejamento' },
  { path: '/quality/operacao', requiredPermission: 'sidebar_quality_operacao' },
  { path: '/cadastros/produto', requiredPermission: 'sidebar_cadastros_produto' },
  { path: '/cadastros/itens-consumo', requiredPermission: 'sidebar_cadastros_itens_consumo' },
  { path: '/cadastros/clientes', requiredPermission: 'sidebar_cadastros_clientes' },
  { path: '/cadastros/formas-pagamento', requiredPermission: 'sidebar_cadastros_formas_pagamento' },
  { path: '/cadastros/parametrizacao/setores', requiredPermission: 'sidebar_production_setores' },
  { path: '/cadastros/parametrizacao/maquinas', requiredPermission: 'sidebar_production_maquinas' },
  { path: '/cadastros/parametrizacao/categorias', requiredPermission: 'sidebar_production_categorias' },
  { path: '/cadastros/parametrizacao/causas', requiredPermission: 'sidebar_production_causas' },
  { path: '/suprimentos/estoque', requiredPermission: 'sidebar_suprimentos_estoque' },
  { path: '/suprimentos/separacoes', requiredPermission: 'sidebar_suprimentos_separacoes' },
  { path: '/suprimentos/movimentacoes', requiredPermission: 'sidebar_suprimentos_movimentacoes' },
  { path: '/suprimentos/parametrizacao', requiredPermission: 'sidebar_suprimentos_parametrizacao' },
  { path: '/suprimentos/notas-fiscais', requiredPermission: 'sidebar_suprimentos_notas_fiscais' },
  { path: '/sales/pdv', requiredPermission: 'sidebar_sales_pdv' },
  { path: '/sales/pedidos', requiredPermission: 'sidebar_sales_pedidos' },
  { path: '/sales/tabela', requiredPermission: 'sidebar_sales_tabela' },
  { path: '/admin-panel', requiredPermission: 'sidebar_admin_panel', superAdminOnly: true },
  { path: '/settings', requiredPermission: 'sidebar_settings' },
];

export const FALLBACK_ACCESSIBLE_PATH = '/profile';

/** Rotas com PermissionRoute no App que não estão em ACCESSIBLE_ROUTES_ORDER. */
const PERMISSION_GATED_EXTRAS: AccessibleRoute[] = [
  { path: '/quality', requiredPermission: 'sidebar_quality' },
  { path: '/suprimentos', requiredPermission: 'sidebar_suprimentos' },
  { path: '/sales', requiredPermission: 'sidebar_sales' },
  { path: '/production/control/monitoramento', requiredPermission: 'sidebar_production_monitoring' },
  { path: '/suprimentos/movimentacoes/historico', requiredPermission: 'sidebar_suprimentos_movimentacoes' },
];

const ROUTES_ALWAYS_ACCESSIBLE = new Set(['/profile', '/manual', '/test']);
const KNOWN_UNGATED_ROUTES = new Set([
  '/production',
  '/production/planning',
  '/production/control',
  '/maintenance/control',
  '/maintenance/control/estoque',
  '/maintenance/control/checklist',
  '/maintenance/control/historico',
  '/product-engineering',
  '/cadastros',
  '/cadastros/categoria',
  '/cadastros/parametrizacao',
  '/cadastros/parametrizacao/horarios',
  '/sensoriamento/ativacao',
]);
const KNOWN_DYNAMIC_ROUTE_PREFIXES = [
  '/product-engineering/eng-produto/montagem/',
  '/maintenance/control/machine/',
];

function normalizePath(path: string): string {
  const trimmed = path.replace(/\/$/, '');
  return trimmed || '/';
}

const GATED_ROUTES_BY_LONGEST_PATH = [...ACCESSIBLE_ROUTES_ORDER, ...PERMISSION_GATED_EXTRAS].sort(
  (a, b) => b.path.length - a.path.length,
);

/**
 * Retorna requisitos de acesso para um path (match exato ou prefixo mais longo).
 * `null` = rota sem gate de permissão (aba pode abrir).
 */
export function resolveRequiredPermissionForPath(
  path: string,
): Pick<AccessibleRoute, 'requiredPermission' | 'superAdminOnly'> | null {
  const normalized = normalizePath(path);
  if (ROUTES_ALWAYS_ACCESSIBLE.has(normalized)) {
    return null;
  }

  for (const route of GATED_ROUTES_BY_LONGEST_PATH) {
    if (normalized === route.path || normalized.startsWith(`${route.path}/`)) {
      return {
        requiredPermission: route.requiredPermission,
        superAdminOnly: route.superAdminOnly,
      };
    }
  }

  return null;
}

export function isKnownRoutePath(path: string): boolean {
  const normalized = normalizePath(path);
  if (ROUTES_ALWAYS_ACCESSIBLE.has(normalized) || KNOWN_UNGATED_ROUTES.has(normalized)) {
    return true;
  }

  if (resolveRequiredPermissionForPath(normalized)) {
    return true;
  }

  return KNOWN_DYNAMIC_ROUTE_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export function canAccessRoutePath(
  path: string,
  hasPermission: (permission: string) => boolean,
  isSuperAdmin: boolean,
): boolean {
  if (!isKnownRoutePath(path)) {
    return false;
  }

  const gate = resolveRequiredPermissionForPath(path);
  if (!gate) return true;
  if (gate.superAdminOnly && !isSuperAdmin) return false;
  if (gate.requiredPermission && !hasPermission(gate.requiredPermission)) return false;
  return true;
}

export function resolveDefaultAccessiblePath(
  hasPermission: (permission: string) => boolean,
  isSuperAdmin: boolean,
): string {
  for (const route of ACCESSIBLE_ROUTES_ORDER) {
    if (route.superAdminOnly && !isSuperAdmin) {
      continue;
    }
    if (route.requiredPermission && !hasPermission(route.requiredPermission)) {
      continue;
    }
    if (isKnownRoutePath(route.path)) {
      return route.path;
    }
  }
  return FALLBACK_ACCESSIBLE_PATH;
}
